import * as React from 'react';
import Markdoc, { type RenderableTreeNode } from '@markdoc/markdoc';
import useIsHydrated from 'hooks/useIsHydrated';
import EmailLink from './EmailLink';

/**
 * Email addresses in CMS content (Sanity Portable Text, editorial Markdoc, table cells) rendered so
 * the server HTML carries no address for Cloudflare to rewrite (see EmailLink).
 *
 * Cloudflare rewrites any address, not only ours, so this matches any address in the text and any
 * `mailto:` link. Before hydration an address reads "team at insiderfinance.io", which keeps the
 * sentence's meaning for crawlers and visitors without JavaScript.
 *
 * - An address in plain text becomes an EmailLink.
 * - An address inside another link becomes plain text (EmailText), never a link inside a link.
 * - A `mailto:` link becomes an EmailLink with its subject and body; its text is kept, except when
 *   the text is just the address.
 */

const ADDRESS = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/;
const ADDRESSES = new RegExp(ADDRESS.source, 'g');
const ONLY_ADDRESS = new RegExp(`^${ADDRESS.source}$`);
const MAILTO = /^mailto:([^?]*)(?:\?(.*))?$/i;

export const spellOutEmail = (email: string) => email.replace(/@/g, ' at ');

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** The recipients (comma-separated) and other fields of a `mailto:` link, or null. */
function parseMailto(href: unknown): { email: string; query?: string } | null {
  const match = typeof href === 'string' ? href.match(MAILTO) : null;
  if (!match) return null;
  const recipients = safeDecode(match[1])
    .split(',')
    .map((address) => address.trim())
    .filter(Boolean);
  if (!recipients.length || !recipients.every((address) => ONLY_ADDRESS.test(address))) {
    return null;
  }
  return { email: recipients.join(','), query: match[2] || undefined };
}

/** Splits `text` into plain pieces and address pieces, in order. */
function splitAddresses(text: string): { text: string; email: string | null }[] {
  const pieces: { text: string; email: string | null }[] = [];
  let last = 0;
  for (const match of text.matchAll(ADDRESSES)) {
    const start = match.index ?? 0;
    if (start > last) pieces.push({ text: text.slice(last, start), email: null });
    pieces.push({ text: match[0], email: match[0] });
    last = start + match[0].length;
  }
  if (last < text.length) pieces.push({ text: text.slice(last), email: null });
  return pieces;
}

/** An address as plain text: spelled out until the page has hydrated. */
export function EmailText({ email }: { email: string }) {
  const isHydrated = useIsHydrated();
  return <>{isHydrated ? email : spellOutEmail(email)}</>;
}

/** A plain string (e.g. a table cell) with each address rendered as an EmailLink. */
export function renderEmailAddresses(text: string): React.ReactNode {
  if (typeof text !== 'string' || !ADDRESS.test(text)) return text;
  return splitAddresses(text).map((piece, index) =>
    piece.email ? (
      <EmailLink key={index} email={piece.email} placeholder={spellOutEmail(piece.email)} />
    ) : (
      piece.text
    ),
  );
}

// ---------------------------------------------------------------------------------------------
// Portable Text

interface PortableSpan {
  _type: string;
  _key?: string;
  text?: string;
  marks?: string[];
}

interface PortableMarkDef {
  _type: string;
  _key: string;
  href?: string;
  email?: string;
  query?: string;
}

interface PortableBlock {
  _type: string;
  _key?: string;
  children?: PortableSpan[];
  markDefs?: PortableMarkDef[];
}

/** Annotations that render a link; an address inside one must not become another link. */
const LINK_MARKS = new Set(['link', 'internalLink', 'emailAddress']);

function linkBlockAddresses(block: PortableBlock): PortableBlock {
  if (block._type !== 'block' || !Array.isArray(block.children)) return block;
  const original = block.markDefs ?? [];
  const markDefs: PortableMarkDef[] = original.map((def) => {
    const mailto = def._type === 'link' ? parseMailto(def.href) : null;
    return mailto ? { _type: 'emailAddress', _key: def._key, ...mailto } : def;
  });
  const linkMarks = new Set(markDefs.filter((def) => LINK_MARKS.has(def._type)).map((d) => d._key));
  let changed = markDefs.some((def, index) => def !== original[index]);

  const children = block.children.flatMap((span, spanIndex) => {
    if (span._type !== 'span' || typeof span.text !== 'string' || !ADDRESS.test(span.text)) {
      return [span];
    }
    changed = true;
    const insideLink = (span.marks ?? []).some((mark) => linkMarks.has(mark));
    return splitAddresses(span.text).flatMap((piece, pieceIndex) => {
      const key = `${span._key ?? spanIndex}-email-${pieceIndex}`;
      if (!piece.email) return [{ ...span, _key: key, text: piece.text }];
      markDefs.push({
        _type: insideLink ? 'emailText' : 'emailAddress',
        _key: key,
        email: piece.email,
      });
      const address = { ...span, _key: key, text: piece.text, marks: [...(span.marks ?? []), key] };
      // The renderer nests the mark that spans more pieces outside; on a tie it falls back to the
      // keys. An empty piece with the original marks keeps them (a link, bold) outside the email
      // mark, whose renderer replaces its text.
      return span.marks?.length ? [address, { ...span, _key: `${key}-end`, text: '' }] : [address];
    });
  });

  return changed ? { ...block, children, markDefs } : block;
}

/** Portable Text with every address and `mailto:` link turned into an email mark. */
export function linkEmailAddresses<T>(value: T): T {
  if (Array.isArray(value)) return value.map((block) => linkBlockAddresses(block)) as unknown as T;
  if (value && typeof value === 'object') {
    return linkBlockAddresses(value as unknown as PortableBlock) as unknown as T;
  }
  return value;
}

interface EmailMarkProps {
  value?: { email?: string; query?: string };
  text?: string;
  children?: React.ReactNode;
}

/** A link to the address; its text, when it is just the address, waits for hydration. */
function EmailAddressMark({ value, text = '', children }: EmailMarkProps) {
  const email = value?.email ?? '';
  return (
    <EmailLink email={email} query={value?.query} placeholder={spellOutEmail(email)}>
      {text.trim() === email ? undefined : children}
    </EmailLink>
  );
}

/** An address inside another link: plain text. */
function EmailTextMark({ value }: EmailMarkProps) {
  return <EmailText email={value?.email ?? ''} />;
}

/** Add to a PortableText `components.marks`, and pass the value through `linkEmailAddresses`. */
export const emailPortableTextMarks = { emailAddress: EmailAddressMark, emailText: EmailTextMark };

// ---------------------------------------------------------------------------------------------
// Markdoc

const childText = (nodes: RenderableTreeNode[]): string =>
  nodes
    .map((node) =>
      typeof node === 'string' ? node : Markdoc.Tag.isTag(node) ? childText(node.children) : '',
    )
    .join('');

function linkTreeChildren(children: RenderableTreeNode[], insideLink: boolean) {
  return children.flatMap((child) =>
    typeof child === 'string' && ADDRESS.test(child)
      ? splitAddresses(child).map((piece) =>
          piece.email
            ? new Markdoc.Tag(insideLink ? 'EmailText' : 'EmailAddress', { email: piece.email }, [])
            : piece.text,
        )
      : [linkTree(child, insideLink)],
  );
}

function linkTree(node: RenderableTreeNode, insideLink: boolean): RenderableTreeNode {
  if (!Markdoc.Tag.isTag(node)) return node;
  if (node.name === 'a') {
    const mailto = parseMailto(node.attributes.href);
    if (mailto) {
      const keepText = childText(node.children).trim() !== mailto.email;
      return new Markdoc.Tag(
        'EmailAddress',
        mailto,
        keepText ? linkTreeChildren(node.children, true) : [],
      );
    }
    return new Markdoc.Tag(node.name, node.attributes, linkTreeChildren(node.children, true));
  }
  return new Markdoc.Tag(node.name, node.attributes, linkTreeChildren(node.children, insideLink));
}

/** A Markdoc render tree with every address and `mailto:` link rendered as above. */
export function linkEmailAddressesInTree(node: RenderableTreeNode): RenderableTreeNode {
  return linkTree(node, false);
}

/** Markdoc component for `EmailAddress` tags. */
export function EmailAddress({
  email,
  query,
  children,
}: {
  email: string;
  query?: string;
  children?: React.ReactNode;
}) {
  const hasChildren = React.Children.count(children) > 0;
  return (
    <EmailLink email={email} query={query} placeholder={spellOutEmail(email)}>
      {hasChildren ? children : undefined}
    </EmailLink>
  );
}

/** Add both to `Markdoc.renderers.react` components, and pass the tree through `linkEmailAddressesInTree`. */
export const emailMarkdocComponents = { EmailAddress, EmailText };
